import * as React from 'react';

import { Tag } from '@coursera/cds-core';
import type { TagProps } from '@coursera/cds-core';
import { TrendingIcon } from '@coursera/cds-icons';

import type { StyledPillTag } from 'bundles/product-card/components/LearningProductCardColorCodedPillTags';
import HotNewReleaseIcon from 'bundles/product-card/components/utils/HotNewReleaseIcon';
import type { MarketingStatusTag } from 'bundles/product-card/components/utils/marketingTagUtils';
import {
  bestsellerColors,
  hotNewReleaseColors,
  recentlyUpdatedColors,
  topAiProgramColors,
  trendingNowColors,
} from 'bundles/product-card/components/utils/marketingTagUtils';

const topAiProgramPillStyle: React.CSSProperties = {
  ...topAiProgramColors,
  border: 'none',
  paddingTop: '1px',
  paddingBottom: '1px',
};

const bestsellerPillStyle: React.CSSProperties = {
  ...bestsellerColors,
  border: 'none',
  paddingTop: '1px',
  paddingBottom: '1px',
};

const hotNewReleasePillStyle: React.CSSProperties = {
  ...hotNewReleaseColors,
  border: 'none',
  paddingTop: '1px',
  paddingBottom: '1px',
};

const recentlyUpdatedPillStyle: React.CSSProperties = {
  ...recentlyUpdatedColors,
  border: 'none',
  paddingTop: '1px',
  paddingBottom: '1px',
};

// CDS's default hasIcon padding-inline-start (--cds-spacing-25, 2px) reads as too tight against
// this icon's own glyph, which — unlike AIGenerateIcon — has ink running close to its left edge.
// paddingLeft below overrides just that side to give the icon breathing room from the pill border.
const trendingNowPillStyle: React.CSSProperties = {
  ...trendingNowColors,
  border: 'none',
  paddingTop: '1px',
  paddingBottom: '1px',
  paddingLeft: 'var(--cds-spacing-75)',
};

/**
 * Supplies the custom-styled pill for the marketing tag categories that have one. `tag` matches
 * the marketing tag's own translated text, which is what slots the renderer into the pill list —
 * the tag itself is never added separately, or the pill would render twice.
 *
 * Categories without a bespoke treatment (job-ready/job-skills) return undefined and
 * fall through to the default CDS Tag rendering.
 */
export const getMarketingStyledPillTag = (
  marketingTag: MarketingStatusTag,
  size?: TagProps['size']
): StyledPillTag | undefined => {
  switch (marketingTag.category) {
    case 'top-ai-program':
      return {
        tag: marketingTag.tag,
        tagRenderer: (
          <Tag color="default" priority="secondary" size={size} variant="status" style={topAiProgramPillStyle}>
            {marketingTag.tag}
          </Tag>
        ),
      };
    case 'trending-now':
      return {
        tag: marketingTag.tag,
        tagRenderer: (
          <Tag
            color="default"
            priority="secondary"
            size={size}
            variant="status"
            icon={<TrendingIcon size="small" />}
            style={trendingNowPillStyle}
          >
            {marketingTag.tag}
          </Tag>
        ),
      };
    case 'bestseller':
      return {
        tag: marketingTag.tag,
        tagRenderer: (
          <Tag color="default" priority="secondary" variant="status" style={bestsellerPillStyle}>
            {marketingTag.tag}
          </Tag>
        ),
      };
    case 'recently-updated':
      return {
        tag: marketingTag.tag,
        tagRenderer: (
          <Tag color="default" priority="secondary" variant="status" style={recentlyUpdatedPillStyle}>
            {marketingTag.tag}
          </Tag>
        ),
      };
    case 'hot-new-release':
      return {
        tag: marketingTag.tag,
        tagRenderer: (
          <Tag
            color="default"
            priority="secondary"
            variant="status"
            icon={<HotNewReleaseIcon />}
            style={hotNewReleasePillStyle}
          >
            {marketingTag.tag}
          </Tag>
        ),
      };
    default:
      return undefined;
  }
};
